import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken, selectStaffId } from '../../../slices/staffSlice';
import { Topbar, Sidebar, Loader, StaffAlias, Deactivate, UpdateRole } from "../../../components";
import { fetchRequests } from "../../../Requests/getRequests";
import { authStaff } from '../../../utils/constants';
import { show } from '../../../slices/toastSlice';
import { useNavigate, Link } from 'react-router-dom';
import ResetMfa from './ResetMfa';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserIcon, ArrowLeft01Icon } from '@hugeicons/core-free-icons';

function Edit() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const staffId = useSelector(selectStaffId);
    const [staff, setStaff] = useState();
    const [roleList, setRoleList] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [roleProcess, setRoleProcess] = useState(false);

    const handleRoleSelect = (e) => {
        const { value, checked } = e.target;
        setSelectedRoles(prev =>
            checked ? [...prev, value] : prev.filter(r => r !== value)
        );
    };

    const updateRoles = async () => {
        setRoleProcess(true);
        const res = await fetchRequests(`/auth/roles/assign`, "POST", authStaff(accessToken), {}, {
            staffId: staff?._id,
            roleIds: selectedRoles,
        });
        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }
        dispatch(show({ type: res?.err ? "error" : "success", state: true, message: res?.err || res?.message || "Roles updated" }));
        setRoleProcess(false);
    };

    useEffect(() => {
        const load = async () => {
            const headers = authStaff(accessToken);
            const [staffRes, rolesRes] = await Promise.all([
                fetchRequests(`/auth/staff/find`, "GET", headers, { _id: staffId }, {}),
                fetchRequests(`/auth/roles/`, "GET", headers, {}, {})
            ]);

            if (staffRes?.code === 401) {
                sessionStorage.clear();
                navigate("/login");
                dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
                return;
            }

            setStaff(staffRes);
            if (!rolesRes?.err) {
                const list = (rolesRes?.roles || []).map(r => ({ roleId: r._id, name: r.slug }));
                setRoleList(list);
                const existing = (staffRes?.roles || []).map(r => r?._id || r).filter(Boolean);
                setSelectedRoles(existing);
            }
        };
        load();
    }, [accessToken, staffId, dispatch, navigate]);

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/staff"
                            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                            <HugeiconsIcon icon={ArrowLeft01Icon} size={15} />
                            Staff
                        </Link>
                        <span className="text-gray-300 dark:text-gray-600">/</span>
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={UserIcon} size={14} className="text-[#1e3a6e] dark:text-blue-400" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                {staff ? `${staff.otherNames} ${staff.surname}` : 'Loading...'}
                            </span>
                            {staff && (
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${staff.isActive ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/30' : 'bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-100 dark:border-red-500/30'}`}>
                                    {staff.isActive ? 'Active' : 'Inactive'}
                                </span>
                            )}
                        </div>
                    </div>

                    {!staff ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader color="text-gray-400" size="w-6 h-6" />
                        </div>
                    ) : (
                        <div className="space-y-5">

                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Edit Information</p>
                                <StaffAlias staff={staff} setStaff={setStaff} />
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Assign Roles</p>
                                <UpdateRole
                                    staff={{ ...staff, roles: selectedRoles.map(id => ({ roleId: id })) }}
                                    roleList={roleList}
                                    handleRoleSelect={handleRoleSelect}
                                    updateRoles={updateRoles}
                                    process={roleProcess}
                                />
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Activate / Deactivate Staff</p>
                                <p className="text-xs text-gray-400 mb-4">
                                    This account is currently <span className={`font-medium ${staff.isActive ? 'text-emerald-600' : 'text-red-500'}`}>{staff.isActive ? 'active' : 'inactive'}</span>.
                                </p>
                                <Deactivate _id={staff?._id} approved={staff?.isActive} />
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Two-Factor Authentication</p>
                                <p className="text-xs text-gray-400 mb-4">
                                    Use this if they lost their phone and their backup codes.
                                </p>
                                <ResetMfa _id={staff?._id} enabled={staff?.mfaEnabled} onReset={() => setStaff({ ...staff, mfaEnabled: false })} />
                            </div>

                        </div>
                    )}

                </section>
            </main>
        </div>
    );
}

export default Edit;
